import React, { useState, useRef, useCallback } from 'react';
import * as XLSX from 'xlsx';
import { submitBatchTranslation, storeRequestId, createBulkBatch, uploadFileToS3 } from '../api';
import { extractJsonLeaves } from '../utils/jsonDocument';
import TmLanguageSelector from './TmLanguageSelector';
import { getColumnLabelsForTm } from '../utils/languages';
import { MAX_SOURCE_UTF8_BYTES, isSourceOverLimit } from '../constants/limits';

const ACCEPTED_EXTENSIONS = ['.xlsx', '.xls', '.json'];

const PREVIEW_PAGE_SIZE = 25;

function BulkUpload({ selectedTmId, availableTMs = [], onTmChange }) {
  const [file, setFile] = useState(null);
  // `rows` is the unified upload-row format used for both XLSX and JSON:
  //   { english: '...', source_path: 'optional dotted path' }
  // For Excel uploads source_path is empty; for JSON uploads it is the
  // dotted path of the leaf within the document so the download flow
  // can rebuild the original JSON tree.
  const [rows, setRows] = useState([]);
  const [sourceFormat, setSourceFormat] = useState('xlsx');
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0, stage: '' });
  const [results, setResults] = useState([]);
  const [error, setError] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  // Pagination on the preview table. Reset to page 0 whenever a new
  // file is loaded so we always show the start of the document.
  const [previewPage, setPreviewPage] = useState(0);
  const fileInputRef = useRef(null);

  const downloadTemplate = () => {
    const templateData = [
      { English: 'Enter your English text here', Translation: '' },
      { English: 'Another sentence to translate', Translation: '' },
    ];
    
    const ws = XLSX.utils.json_to_sheet(templateData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Translations');
    
    ws['!cols'] = [{ wch: 50 }, { wch: 50 }];
    
    XLSX.writeFile(wb, 'translation_template.xlsx');
  };

  // Excel rows -> unified [{ english, source_path }] shape. source_path
  // stays empty because spreadsheet uploads have no document structure
  // to preserve.
  const validateExcelRows = (data) => {
    if (!data || data.length === 0) {
      return { valid: false, error: 'File is empty' };
    }

    const firstRow = data[0];
    const hasEnglishColumn = 'English' in firstRow || 'english' in firstRow || 'ENGLISH' in firstRow;

    if (!hasEnglishColumn) {
      return {
        valid: false,
        error: 'File must have an "English" column. Please use the template.'
      };
    }

    const validRows = data
      .map((row) => {
        const text = row.English || row.english || row.ENGLISH;
        return text ? { english: text.toString().trim(), source_path: '' } : null;
      })
      .filter((row) => row && row.english.length > 0);

    if (validRows.length === 0) {
      return { valid: false, error: 'No valid rows found with English text' };
    }

    return { valid: true, rows: validRows };
  };

  // JSON parsing path. We translate every string leaf in the document
  // and remember its dotted path so the download flow can rebuild the
  // tree with Spanish values in place. Keys are preserved as-is - only
  // string values are translated.
  const validateJsonDocument = (text) => {
    let parsed;
    try {
      parsed = JSON.parse(text);
    } catch (err) {
      return { valid: false, error: 'Invalid JSON: ' + err.message };
    }

    const leaves = extractJsonLeaves(parsed);
    if (leaves.length === 0) {
      return { valid: false, error: 'JSON document contains no string values to translate.' };
    }

    return {
      valid: true,
      rows: leaves.map((l) => ({ english: l.value, source_path: l.path })),
    };
  };

  const handleFileProcess = useCallback((selectedFile) => {
    setError(null);
    setResults([]);
    setRows([]);
    setPreviewPage(0);

    if (!selectedFile) {
      setFile(null);
      return;
    }

    const fileExt = selectedFile.name.toLowerCase().slice(selectedFile.name.lastIndexOf('.'));

    if (!ACCEPTED_EXTENSIONS.includes(fileExt)) {
      setError('Please upload an Excel file (.xlsx, .xls) or JSON file (.json).');
      setFile(null);
      return;
    }

    const isJson = fileExt === '.json';
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        let validation;
        if (isJson) {
          validation = validateJsonDocument(event.target.result);
        } else {
          const data = new Uint8Array(event.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet);
          validation = validateExcelRows(jsonData);
        }

        if (!validation.valid) {
          setError(validation.error);
          setFile(null);
          return;
        }

        setFile(selectedFile);
        setRows(validation.rows);
        setSourceFormat(isJson ? 'json' : 'xlsx');
      } catch (err) {
        setError(`Failed to parse ${isJson ? 'JSON' : 'Excel'} file: ${err.message}`);
        setFile(null);
      }
    };

    if (isJson) {
      reader.readAsText(selectedFile);
    } else {
      reader.readAsArrayBuffer(selectedFile);
    }
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files).filter((f) => {
      const lower = f.name.toLowerCase();
      return ACCEPTED_EXTENSIONS.some((ext) => lower.endsWith(ext));
    });
    if (files.length > 0) {
      handleFileProcess(files[0]);
    }
  }, [handleFileProcess]);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    handleFileProcess(selectedFile);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const processTranslations = async () => {
    if (rows.length === 0 || !file) return;

    setProcessing(true);
    setProgress({ current: 0, total: rows.length + 1, stage: 'Creating batch...' });
    setResults([]);
    setError(null);

    let batchId = null;
    const isJson = sourceFormat === 'json';

    try {
      const selectedTm = availableTMs.find((t) => t.tm_id === selectedTmId) || {};
      const batchResponse = await createBulkBatch(file.name, rows.length, sourceFormat, {
        tm_id: selectedTmId,
        targetLanguage: selectedTm.targetLanguage || 'es-US',
        program: selectedTm.program || 'domestic',
        category: selectedTm.category || 'general',
      });
      batchId = batchResponse.batch_id;

      if (batchResponse.upload_url) {
        setProgress({
          current: 0,
          total: rows.length + 1,
          stage: `Uploading your ${isJson ? 'JSON' : 'Excel'} file...`,
        });
        await uploadFileToS3(
          batchResponse.upload_url,
          file,
          isJson ? 'application/json' : undefined,
        );
      }
    } catch (err) {
      setError('Failed to create batch: ' + err.message);
      setProcessing(false);
      return;
    }

    const processedResults = [];

    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      const englishText = row.english;

      if (isSourceOverLimit(englishText)) {
        processedResults.push({
          rowIndex: i + 1,
          english: englishText,
          reqId: null,
          status: 'error',
          error: `Source text is too long (exceeds ${MAX_SOURCE_UTF8_BYTES.toLocaleString()} UTF-8 bytes). Please split into smaller sections.`,
          spanish: '',
        });
        setProgress({
          current: i + 1,
          total: rows.length,
          message: `Row ${i + 1}: skipped (text too long)`,
        });
        continue;
      }

      try {
        const selectedTm = availableTMs.find((t) => t.tm_id === selectedTmId) || {};
        const result = await submitBatchTranslation({
          source: englishText,
          sourceLanguage: 'en-US',
          targetLanguage: selectedTm.targetLanguage || 'es-US',
          category: selectedTm.category || 'general',
          program: selectedTm.program || 'domestic',
          tm_id: selectedTmId,
          batch_id: batchId,
          sourcePath: row.source_path || '',
        });

        if (result.req_id) {
          storeRequestId(result.req_id);
        }

        processedResults.push({
          rowIndex: i + 1,
          english: englishText,
          reqId: result.req_id,
          status: 'submitted',
          spanish: '',
        });
      } catch (err) {
        processedResults.push({
          rowIndex: i + 1,
          english: englishText,
          reqId: null,
          status: 'error',
          error: err.message,
          spanish: '',
        });
      }

      setProgress({
        current: i + 1,
        total: rows.length,
        stage: `Uploading your file. Processing item ${i + 1} of ${rows.length}.`,
      });
      setResults([...processedResults]);

      if (i < rows.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 200));
      }
    }

    setProcessing(false);
  };

  const resetUpload = () => {
    setFile(null);
    setRows([]);
    setResults([]);
    setError(null);
    setSourceFormat('xlsx');
    setProgress({ current: 0, total: 0, stage: '' });
    setPreviewPage(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const submittedCount = results.length;
  const submitErrorCount = results.filter(r => r.status === 'error').length;

  return (
    <div className="bulk-upload-page">
      {/* Step 1: Upload */}
      <div className="card">
        <div className="card-header">
          <div className="card-header-content">
            <div className="step-number">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="17 8 12 3 7 8" />
                <line x1="12" y1="3" x2="12" y2="15" />
              </svg>
            </div>
            <div className="card-header-text">
              <h2>Translate files</h2>
              <p>Translate multiple text entries using a spreadsheet or json file</p>
            </div>
            <div className="card-header-helper">
              Best for translating many text entries at once.
            </div>
          </div>
        </div>
        <div className="card-body">
          <div className="template-section">
            <div className="template-section-text">
              <p><strong>For spreadsheet translations:</strong></p>
              <p>Download the template and enter one text item per row.</p>
              <p className="template-section-hint">
                Keep the first two columns labeled <strong>English</strong> and <strong>Translation</strong>.
                Do not rename or remove them.
              </p>
              <p style={{ marginTop: '0.75rem' }}><strong>For JSON files:</strong></p>
              <p>Upload the file in the box below.</p>
            </div>
            <div className="template-section-action">
              <button className="btn btn-outline" onClick={downloadTemplate}>
                ↓ Download spreadsheet template
              </button>
              <div className="template-section-button-helper">
                Use this template to ensure correct formatting.
              </div>
            </div>
          </div>

          <div className="tm-selector-block">
            <TmLanguageSelector
              selectedTmId={selectedTmId}
              availableTMs={availableTMs}
              onTmChange={onTmChange}
              disabled={processing}
              idPrefix="bulk"
            />
          </div>

          <div className="upload-file-section">
            <div className="form-label">Upload File</div>
            <div className="form-label" style={{ fontSize: '0.8rem', color: 'var(--brand-neutral-500)', marginBottom: '0.75rem' }}>
              Excel (.xlsx, .xls) with one item per row, or a JSON document
              (.json) - every string value is translated and the original keys
              are preserved on download.
            </div>
            <div
              className={`upload-zone ${isDragging ? 'dragging' : ''}`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              <svg className="upload-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="17 8 12 3 7 8" />
                <line x1="12" y1="3" x2="12" y2="15" />
              </svg>
              <p className="upload-text">
                {isDragging ? 'Drop file here' : 'Drag and drop your spreadsheet or JSON file here'}
              </p>
              <p className="upload-subtext">or</p>
              <button 
                className="btn btn-primary"
                onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}
                disabled={processing}
              >
                Choose File
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls,.json"
                onChange={handleFileChange}
                style={{ display: 'none' }}
                disabled={processing}
              />
            </div>
            {file && (
              <div className="file-info" style={{ marginTop: '0.75rem' }}>
                ✓ File selected: {rows.length} rows ready for translation ({file.name})
              </div>
            )}
          </div>

          {error && (
            <div className="error-banner">{error}</div>
          )}
        </div>
      </div>

      {/* Step 2: Preview & Process */}
      {rows.length > 0 && results.length === 0 && (
        <div className="card">
          <div className="card-header">
            <div className="card-header-content">
              <div className="step-number">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
              </div>
              <div className="card-header-text">
                <h2>Preview &amp; Submit</h2>
                <p>Click on Start Translation to begin</p>
              </div>
            </div>
          </div>
          <div className="card-body">
            {(() => {
              const totalPreviewPages = Math.max(1, Math.ceil(rows.length / PREVIEW_PAGE_SIZE));
              const safePreviewPage = Math.min(previewPage, totalPreviewPages - 1);
              const previewStart = safePreviewPage * PREVIEW_PAGE_SIZE;
              const previewEnd = Math.min(previewStart + PREVIEW_PAGE_SIZE, rows.length);
              const previewSlice = rows.slice(previewStart, previewEnd);
              const previewRangeStart = rows.length === 0 ? 0 : previewStart + 1;
              const previewSourceLabel = getColumnLabelsForTm(
                availableTMs.find((t) => t.tm_id === selectedTmId) || {},
              ).source;
              return (
                <div className="preview-section">
                  <h3>Preview ({rows.length} rows)</h3>
                  <p className="preview-section-helper">
                    This preview shows the text that will be translated.
                    Translations will appear after processing is complete.
                    {rows.length > PREVIEW_PAGE_SIZE && (
                      <>
                        {' '}Use the controls below to page through the file.
                      </>
                    )}
                  </p>
                  <div className="preview-table-container">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>#</th>
                          {sourceFormat === 'json' && <th>JSON Path</th>}
                          <th>{previewSourceLabel} Text</th>
                        </tr>
                      </thead>
                      <tbody>
                        {previewSlice.map((row, idx) => {
                          const absoluteIdx = previewStart + idx;
                          return (
                            <tr key={absoluteIdx}>
                              <td>{absoluteIdx + 1}</td>
                              {sourceFormat === 'json' && (
                                <td><code style={{ fontSize: '0.8rem' }}>{row.source_path}</code></td>
                              )}
                              <td>
                                {row.english.substring(0, 100)}
                                {row.english.length > 100 ? '...' : ''}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {totalPreviewPages > 1 && (
                    <div className="pagination-bar pagination-bar--inner">
                      <button
                        type="button"
                        className="btn btn-ghost btn-small"
                        onClick={() => setPreviewPage(Math.max(0, safePreviewPage - 1))}
                        disabled={safePreviewPage === 0}
                      >
                        ← Previous
                      </button>
                      <span className="pagination-info">
                        Showing {previewRangeStart}-{previewEnd} of {rows.length}
                        {' · '}Page {safePreviewPage + 1} of {totalPreviewPages}
                      </span>
                      <button
                        type="button"
                        className="btn btn-ghost btn-small"
                        onClick={() => setPreviewPage(Math.min(totalPreviewPages - 1, safePreviewPage + 1))}
                        disabled={safePreviewPage >= totalPreviewPages - 1}
                      >
                        Next →
                      </button>
                    </div>
                  )}
                </div>
              );
            })()}

            <div className="bulk-actions" style={{ marginTop: '1.5rem', justifyContent: 'space-between' }}>
              <button className="btn btn-ghost" onClick={resetUpload} disabled={processing}>
                ← Start Over
              </button>
              <button 
                className="btn btn-primary btn-lg" 
                onClick={processTranslations}
                disabled={processing}
              >
                {processing ? (
                  <>
                    <span className="spinner"></span>
                    Processing...
                  </>
                ) : (
                  `Start Translation (${rows.length} item${rows.length === 1 ? '' : 's'})`
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Processing Progress */}
      {processing && (
        <div className="card">
          <div className="card-body">
            <div className="progress-section">
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                <span style={{ fontWeight: 600, color: 'var(--brand-secondary-700)' }}>
                  {progress.stage}
                </span>
                <span style={{ fontWeight: 700, color: 'var(--brand-primary-600)' }}>
                  {progress.total > 0 ? Math.round((progress.current / progress.total) * 100) : 0}%
                </span>
              </div>
              <div className="progress-bar" style={{ height: '12px' }}>
                <div 
                  className="progress-fill" 
                  style={{ width: `${progress.total > 0 ? (progress.current / progress.total) * 100 : 0}%` }}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Submission complete - status, review and download all live in
          the My Translations - Files tab now (mirrors the section flow). */}
      {results.length > 0 && !processing && (
        <div className="card">
          <div className="card-header">
            <div className="card-header-content">
              <div className="step-number">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                  <polyline points="22 4 12 14.01 9 11.01" />
                </svg>
              </div>
              <div className="card-header-text">
                <h2>Submission complete</h2>
                <p>Your file is being translated.</p>
              </div>
            </div>
          </div>
          <div className="card-body">
            <div className="submission-complete-banner">
              <div className="submission-complete-icon" aria-hidden="true">✓</div>
              <div className="submission-complete-text">
                <h3>Submitted {submittedCount} translation{submittedCount === 1 ? '' : 's'}</h3>
                <p>
                  Go to the <strong>My Translations - Files</strong> tab to see status,
                  review individual translations, and download the file once translations
                  are complete.
                </p>
                {submitErrorCount > 0 && (
                  <p className="submission-complete-warning">
                    {submitErrorCount} item{submitErrorCount === 1 ? '' : 's'} could not be submitted. They will appear with a Translation Error status.
                  </p>
                )}
              </div>
            </div>

            <div className="bulk-actions" style={{ marginTop: '1.5rem', justifyContent: 'flex-end' }}>
              <button
                className="btn btn-primary"
                onClick={resetUpload}
              >
                Translate another file
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default BulkUpload;
